import { PermissionsTypes } from '@back-office/http';
import {
  ChangeEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { GroupedPermissions } from '.';
import { PERMISSION_VOID_REFUND_MAXIMUM } from '../../common';

type UseViewModelProps = {
  currentPermission: number[];
  handleOnChange: (
    currentPermission: number[],
    newPermissions: number[],
    isVoidRefundChecked: boolean
  ) => void;
  voidRefundCount: number;
  isRecurringMerchant: boolean;
  hiddenField: boolean;
  permissionsList: PermissionsTypes[];
};

type UseViewModelReturn = {
  anchorEl: HTMLButtonElement;
  groupedPermissions: GroupedPermissions;
  handleCheckboxChange: (
    event: ChangeEvent<HTMLInputElement>,
    id: string
  ) => void;
  handleRecurringRequestChange: (value: string) => void;
  handleUploadPaymentChange: (value: string) => void;
  handleClick: (event: SyntheticEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  isVoidRefundChecked: boolean;
  labelMapPermissions: (permission: string) => string;
  menuStyles: { marginTop: string };
  newPermissions: string[];
  refundPermission: string;
  recurringPermission: string;
  selectedPermissionsCount: number;
  uploadPaymentPermission: string;
  changeToPermissionFormat: (input: string) => string;
};

const useViewModel = ({
  currentPermission,
  handleOnChange,
  voidRefundCount,
  isRecurringMerchant,
  hiddenField,
  permissionsList,
}: UseViewModelProps): UseViewModelReturn => {
  // Constants for permission categories
  const REFUND_PERMISSIONS = ['1'];
  const RECURRING_REQUEST_PERMISSIONS = ['2', '3'];
  const PAYMENT_UPLOAD_PERMISSIONS = ['7', '4', '5', '6'];

  // Function to convert permission names
  const changeToPermissionFormat = (input: string): string =>
    input?.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  // Function to get localized label for permission
  const labelMapPermissions = (permission: string): string => {
    const label = permissionsList.find(
      (item) => `${item.id}` === permission
    ).name;
    return `invite_new_user.permissions.${changeToPermissionFormat(label)}`;
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);

  const [groupedPermissions, setGroupedPermissions] =
    useState<GroupedPermissions>({
      paymentUpload: [],
      recurringRequest: [],
      refund: [],
    });

  const groupPermissions = (): void => {
    const paymentUpload = [];
    const recurringRequest = [];
    const refund = [];

    permissionsList.forEach((permission) => {
      const firstName = permission.name.split('.')[0];
      switch (firstName) {
        case 'payment':
          paymentUpload.push(permission);
          break;
        case 'recurring':
          recurringRequest.push(permission);
          break;
        case 'transaction':
          refund.push(permission);
          break;
        default:
          break;
      }
    });

    paymentUpload.sort(
      (a, b) =>
        PAYMENT_UPLOAD_PERMISSIONS.indexOf(`${a.id}`) -
        PAYMENT_UPLOAD_PERMISSIONS.indexOf(`${b.id}`)
    );

    setGroupedPermissions({ paymentUpload, recurringRequest, refund });
  };

  const isHavePermission = (targetValues: string[]): string => {
    const permission: string | undefined = currentPermission
      .find((id) => targetValues.includes(id.toString()))
      ?.toString();
    return permission || '0';
  };

  const [newPermissions, setNewPermissions] = useState<string[]>([]);
  const [refundPermission, setRefundPermission] = useState(
    isHavePermission(REFUND_PERMISSIONS)
  );
  const [recurringPermission, setRecurringPermission] = useState(
    isHavePermission(RECURRING_REQUEST_PERMISSIONS)
  );
  const [uploadPaymentPermission, setUploadPaymentPermission] = useState(
    isHavePermission(PAYMENT_UPLOAD_PERMISSIONS)
  );

  // State for void/refund validation
  const [isVoidRefundChecked, setIsVoidRefundChecked] = useState<boolean>(
    currentPermission.includes(1)
  );

  const handleCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>,
    id: string
  ): void => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setNewPermissions((prevPermissions) => [...prevPermissions, id]);
      setRefundPermission(id);

      if (id === '1') {
        setIsVoidRefundChecked(!isVoidRefundChecked);
      }
    } else {
      setNewPermissions((prevPermissions) =>
        prevPermissions.filter((permissionId) => permissionId !== id)
      );
      setRefundPermission('0');

      if (id === '1') {
        setIsVoidRefundChecked(!isVoidRefundChecked);
      }
    }
  };

  const handlePermissionChange = (
    value: string,
    permissionList: string[]
  ): void => {
    if (value === '0') {
      setNewPermissions([
        ...newPermissions.filter(
          (permission) => !permissionList.includes(permission)
        ),
      ]);
    } else {
      setNewPermissions([
        ...newPermissions.filter(
          (permission) => !permissionList.includes(permission)
        ),
        value,
      ]);
    }
  };

  const handleRecurringRequestChange = (value: string): void => {
    setRecurringPermission(value);
    handlePermissionChange(value, RECURRING_REQUEST_PERMISSIONS);
  };

  const handleUploadPaymentChange = (value: string): void => {
    setUploadPaymentPermission(value);
    handlePermissionChange(value, PAYMENT_UPLOAD_PERMISSIONS);
  };

  const menuStyles = {
    marginTop:
      anchorEl &&
      anchorEl.getBoundingClientRect().bottom > window.innerHeight / 2
        ? '-8px'
        : '8px',
  };

  const handleClick = (event: SyntheticEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
    groupPermissions();
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const selectedPermissionsCount = useMemo(() => {
    if (isRecurringMerchant) {
      return newPermissions.length;
    }
    // should not show recurring and payment upload permission
    const recurringAndPaymentUploadPermissions = [
      ...RECURRING_REQUEST_PERMISSIONS,
      ...PAYMENT_UPLOAD_PERMISSIONS,
    ];

    const removeRecurringAndPaymentUploadPermissions: string[] =
      newPermissions.filter(
        (permission) =>
          !recurringAndPaymentUploadPermissions.includes(permission)
      );

    return removeRecurringAndPaymentUploadPermissions.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPermissions]);

  useEffect(() => {
    const newPermissionsNumber: number[] = newPermissions
      ? newPermissions.map(Number)
      : [];

    const hasVoidPermission = currentPermission.includes(1);

    handleOnChange(
      currentPermission,
      newPermissionsNumber,
      voidRefundCount >= PERMISSION_VOID_REFUND_MAXIMUM &&
        isVoidRefundChecked &&
        !hasVoidPermission
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPermissions]);

  useEffect(() => {
    setNewPermissions(currentPermission.map((id) => id.toString()));
    setRefundPermission(isHavePermission(REFUND_PERMISSIONS));
    setRecurringPermission(isHavePermission(RECURRING_REQUEST_PERMISSIONS));
    setUploadPaymentPermission(isHavePermission(PAYMENT_UPLOAD_PERMISSIONS));
    setIsVoidRefundChecked(currentPermission.includes(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPermission, hiddenField]);

  return {
    anchorEl,
    groupedPermissions,
    handleCheckboxChange,
    handleRecurringRequestChange,
    handleUploadPaymentChange,
    handleClick,
    handleClose,
    isVoidRefundChecked,
    labelMapPermissions,
    menuStyles,
    newPermissions,
    refundPermission,
    recurringPermission,
    selectedPermissionsCount,
    uploadPaymentPermission,
    changeToPermissionFormat,
  };
};

export default useViewModel;
